import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { createRoomType, deleteRoomType, listRoomTypes, RoomType, updateRoomType } from '../../api/hotels.ts';
import { toastError, toastSuccess } from '../../shared/ui/toast.ts';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const RoomTypesTab: React.FC<{ hotelId: number }> = ({ hotelId }) => {
  const [rows, setRows] = useState<RoomType[]>([]);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<Partial<RoomType> | null>(null);

  const load = async () => {
    try {
      const types = await listRoomTypes(hotelId);
      setRows(Array.isArray(types) ? types : []);
    } catch (e:any) {
      toastError('Failed to load room types');
    }
  };

  useEffect(()=>{ load(); },[hotelId]);

  const onSave = async () => {
    if (!editRow) return;
    try {
      if (editRow.id) {
        await updateRoomType(hotelId, editRow.id, editRow);
        toastSuccess('Saved');
      } else {
        await createRoomType(hotelId, editRow);
        toastSuccess('Created');
      }
      setOpen(false); setEditRow(null); load();
    } catch (e:any) {
      const msg = e?.response?.data?.error || 'Save failed';
      toastError(msg);
    }
  };

  const onDelete = async (id:number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete room type?')) return;
    try { await deleteRoomType(hotelId, id); toastSuccess('Deleted'); load(); } catch (e:any) { toastError('Delete failed'); }
  };

  return (
    <Box mt={2}>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Button variant="outlined" onClick={() => {
          const ts = new Date();
          const header = 'id,hotelId,name,adults,children,bedType,basePrice';
          const rowsCsv = rows.map(r => `${r.id||''},${hotelId},"${r.name}",${r.capacityAdults||0},${r.capacityChildren||0},"${r.bedType||''}",${r.basePrice||0}`);
          const csv = [header].concat(rowsCsv).join('\n');
          const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const pad = (n:number)=> String(n).padStart(2,'0');
          a.href = url; a.download = `roomtypes_h${hotelId}_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}.csv`; a.click();
          URL.revokeObjectURL(url);
        }}>Export CSV</Button>
        <Button variant="contained" onClick={()=>{ setEditRow({ hotelId, name:'', basePrice:0 }); setOpen(true); }}>Add</Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Adults</TableCell>
            <TableCell>Children</TableCell>
            <TableCell>Bed</TableCell>
            <TableCell>Price</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id}>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.capacityAdults}</TableCell>
              <TableCell>{r.capacityChildren}</TableCell>
              <TableCell>{r.bedType}</TableCell>
              <TableCell>{r.basePrice}</TableCell>
              <TableCell align="right">
                <IconButton onClick={()=>{ setEditRow(r); setOpen(true); }} size="small"><EditIcon fontSize="small"/></IconButton>
                <IconButton onClick={()=> onDelete(r.id!)} size="small" color="error"><DeleteIcon fontSize="small"/></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={()=>setOpen(false)}>
        <DialogTitle>{editRow?.id ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Name" value={editRow?.name||''} onChange={(e)=> setEditRow({ ...editRow!, name:e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="Adults" value={editRow?.capacityAdults||0} onChange={(e)=> setEditRow({ ...editRow!, capacityAdults:Number(e.target.value) })} />
              <TextField type="number" label="Children" value={editRow?.capacityChildren||0} onChange={(e)=> setEditRow({ ...editRow!, capacityChildren:Number(e.target.value) })} />
            </Stack>
            <TextField label="Bed type" value={editRow?.bedType||''} onChange={(e)=> setEditRow({ ...editRow!, bedType:e.target.value })} />
            <TextField type="number" label="Base price" value={editRow?.basePrice||0} onChange={(e)=> setEditRow({ ...editRow!, basePrice:Number(e.target.value) })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={onSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomTypesTab;


